import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NowName from '../shared/NowName';
import { Plus, X } from 'lucide-react';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../shared/formValidation';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import AddendumRow, { sortAddendaNewestFirst } from './AddendumRow';
import { SectionCard } from '../workflow/SectionCard';
import { coverageList, coverageListItem } from '../workflow/constants';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { QcadForm, buildQcadBody } from '../../pages/WorkflowQcadNew';
import { emptyAddendumForm, addendumRequiredGaps, addendumRuleProblems, titleFor, eqrLabel, engagementLabelOf } from '../../pages/qcAddendum';

// Management → QC record → its addenda (docs/modules/workflow-qcad.md).
// Lists every addendum filed on the record (workflow-approved or recorded
// here), shows the CURRENT risk rating, and — for qm:qc_records:edit — lets
// a manager record an addendum that was signed outside the system (no
// review; the approval date is typed in, same as a manual QC record).
const QcAddendaSection = ({ record, canEdit = false, onChanged = null }) => {
  const [addenda, setAddenda] = useState([]);
  const [risk, setRisk] = useState(null);
  const [eqr, setEqr] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/qm/qc-records/${record.id}/addenda`);
      setAddenda(data.addenda || []);
      setRisk(data.risk || null);
      setEqr(data.eqr || null);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [record.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <SectionCard
      title={`Addenda (${addenda.length})`}
      actions={canEdit && record.approvedOn ? (
        <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
          <Plus className="w-4 h-4" /> Add Addendum
        </button>
      ) : null}
    >
      {error && <p className="text-sm text-red-600">{error}</p>}
      {risk && (risk.byEngagement || []).length > 1 ? (
        <div className="text-sm text-slate-700 mb-1">
          <div>Current risk rating per engagement:</div>
          <ul className={`mt-1 ${coverageList}`}>
            {risk.byEngagement.map((b) => (
              <li key={b.engagementId} className={coverageListItem}>
                {engagementLabelOf(b)}: <span className="font-semibold">{b.rating || '—'}</span>
                {b.revisedBy ? <span className="text-xs text-slate-500"> — revised by {b.revisedBy}; originally {b.original || '—'}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : risk && (
        <p className="text-sm text-slate-700 mb-1">
          Current risk rating: <span className="font-semibold">{risk.rating || '—'}</span>
          {risk.revisedBy ? <span className="text-xs text-slate-500"> — revised by {risk.revisedBy}; originally {risk.original || '—'}</span> : null}
        </p>
      )}
      {eqr && (
        <p className="text-sm text-slate-700 mb-2">
          Current engagement quality reviewer: <span className="font-semibold">
            <NowName value={eqrLabel(eqr.eqr)} now={eqr.eqrNow} />
          </span>
          {eqr.revisedBy ? <span className="text-xs text-slate-500"> — set by {eqr.revisedBy}; the form named {eqrLabel(eqr.original)}</span> : null}
        </p>
      )}
      {canEdit && !record.approvedOn && (
        <p className="text-xs text-amber-700 mb-2">Set the record's approval date before adding an addendum.</p>
      )}
      {addenda.length === 0 ? (
        <p className="text-sm text-slate-500">No addendum filed on this record.</p>
      ) : (
        <div className="space-y-2">
          {sortAddendaNewestFirst(addenda).map((a, i) => (
            <AddendumRow
              key={a.id}
              addendum={a}
              record={record}
              latest={i === 0}
              pdfHref={`${API_BASE_URL}/api/qm/qc-records/${record.id}/addenda/${a.id}/pdf`}
              fileHref={(f) => `${API_BASE_URL}/api/qm/qc-records/${record.id}/addenda/${a.id}/files/${f.id}`}
              onDelete={canEdit && a.source === 'manual' ? () => setDeleting(a) : null}
            />
          ))}
        </div>
      )}

      {adding && (
        <AddAddendumModal record={record} risk={risk} eqr={eqr} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); if (onChanged) onChanged(); }} />
      )}
      {deleting && (
        <ConfirmDeleteModal
          title="Delete Addendum"
          message="Remove this manually recorded addendum and its files? Addenda produced by an approved request cannot be deleted."
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await fetchJSON(`/api/qm/qc-records/${record.id}/addenda/${deleting.id}`, { method: 'DELETE' });
            setDeleting(null); load(); if (onChanged) onChanged();
          }}
        />
      )}
    </SectionCard>
  );
};

const AddAddendumModal = ({ record, risk, eqr = null, onClose, onDone }) => {
  const [form, setForm] = useState(emptyAddendumForm());
  const [approvedOn, setApprovedOn] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const riskFrom = risk?.rating || null;

  // Every required control still empty, keyed to the control — after the first
  // Save the modal marks them and lists them in the banner. A manual addendum
  // is not reviewed, so no QMP is asked for; the approval date is this modal's own.
  const gaps = useMemo(() => [
    ...addendumRequiredGaps(form, { needsQmp: false, byEngagement: risk && risk.byEngagement }),
    ...(approvedOn ? [] : [{ key: 'approvedOn', label: 'Approval Date' }]),
  ], [form, approvedOn, risk]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    // A filled-in rating that does not move away from the current one.
    const problems = addendumRuleProblems(form, { riskFrom, byEngagement: risk?.byEngagement });
    if (problems.length > 0) { setError(problems[0]); return; }
    setError(null);
    setSaving(true);
    try {
      const fd = buildQcadBody(form, { files });
      fd.set('approvedOn', approvedOn);
      await fetchJSON(`/api/qm/qc-records/${record.id}/addenda`, { method: 'POST', body: fd });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Record an Addendum — ${titleFor(record.qcType)}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
      {(error || validation.error) && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
          {error && <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>}
        </div>
      )}
      <p className="text-xs text-slate-500">Records an addendum that was prepared and approved outside the system. It is filed on the record immediately — no review.</p>
      {/* The identity anchor hands each control its canonical key, so the
          gaps above land on the controls they name. */}
      <QcadForm record={record} risk={risk} eqr={eqr} form={form} setForm={setForm} anchor={(k) => k} manual />
      <Field label="Approval Date" required field="approvedOn" hint="The date the signed addendum was approved — printed on the PDF.">
        <input type="date" value={approvedOn} onChange={(e) => setApprovedOn(e.target.value)} className={`${inputCls} max-w-xs`} />
      </Field>
      <Field label="Supporting Files">
        <FilePicker multiple kind="docsSheetsImages" onChange={(e) => setFiles(Array.from(e.target.files || []))} files={files} />
      </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default QcAddendaSection;
